import React, {useState} from "react";
import {Label, Radio, TextInput, Tooltip} from "flowbite-react";
import {RenderableTitle} from "data/models/survey/base";
import {
  CHOICE_ITEM_CLASS,
  DESCRIPTION_CLASS,
  QUESTION_CONTAINER_CLASS,
  TEXT_INPUT_THEME,
  TITLE_CLASS
} from "components/Survey/classes";
import {
  ClientLocation_SurveyQuestionPage_Options,
  ClientLocationKey,
  ClientLocationValue
} from "data/models/survey/client-location";

import {DefaultTitle} from "components/Survey/survey-sections-rendering-components/SurveySection";

export type ClientLocation_SurveyQuestionPage_Props = {
  options: ClientLocation_SurveyQuestionPage_Options;
  questionId: string;
  required: boolean;
  title: RenderableTitle;
  value: ClientLocationValue | undefined;
  onChange: (id: string, value: ClientLocationValue | null) => void;
}

export default function ClientLocationSurveyQuestionPage(
    {
      options,
      title,
      questionId,
      required,
      value,
      onChange
    }: ClientLocation_SurveyQuestionPage_Props
) {
  const [location, setLocation] = useState(value?.location);
  const [targetArea, setTargetArea] = useState(value?.targetArea);

  function handleChange(changedLocation: ClientLocationKey | undefined, editedTargetArea: string | null | undefined) {
    if (changedLocation !== undefined) {
      setLocation(changedLocation);
    }
    if (editedTargetArea !== undefined) {
      setTargetArea(editedTargetArea);
    }
    if (changedLocation !== undefined
        && (
            changedLocation === "anywhere"
            || editedTargetArea
        )
    ) {
      onChange(questionId, {
        location: changedLocation,
        targetArea: editedTargetArea ?? null,
      });
    } else {
      onChange(questionId, null);
    }
  }

  const renderedTargetAreaTitle = <DefaultTitle title={"What is your target area?"} className={TITLE_CLASS} />;
  const renderedDescription = <DefaultTitle title={"List your city, state, region, etc."} className={DESCRIPTION_CLASS} />;

  return <div className={"flex flex-col w-full"}>
    <div className={QUESTION_CONTAINER_CLASS}>
      <fieldset name={questionId} className={"flex gap-4 mt-4 flex-col"} autoFocus={true}>
        <div className="my-8 text-center">{title}</div>
        {options.choices.map((choice, index) => <div key={index} className="flex flex-col items-center gap-2">
          <Tooltip
              content={<span className={"text-sm"}>{choice.tooltip}</span>}
          >
          <Radio
              className="hidden peer"
              id={`${questionId}.${choice.key}`}
              value={choice.key}
              checked={location === choice.key}
              onChange={({target: {value: changedValue}}) => handleChange(changedValue as ClientLocationKey | undefined, targetArea)}
          />
          <Label htmlFor={`${questionId}.${choice.key}`}
                 className={CHOICE_ITEM_CLASS}>
            {choice.value}
          </Label>
          </Tooltip>
        </div>)}
        {(location === "local" || location === "local-growing") && (
            <div className={"flex flex-row"}>
              <div className={"flex flex-col mt-16 mb-12 grow max-w-xl mx-auto"}>
                <div className="mb-2 text-center">
                  <Label htmlFor={questionId}>{renderedTargetAreaTitle}</Label>
                  <div className={"my-2"}>{renderedDescription}</div>
                </div>
                <TextInput
                    theme={TEXT_INPUT_THEME}
                    sizing={"lg"}
                    id={`${questionId}.target-area`}
                    type={"text"}
                    placeholder={"Region name"}
                    required={required}
                    value={value?.targetArea ?? ""}
                    onChange={({target: {value: editValue}}) => handleChange(location, editValue)}
                />
              </div>
            </div>
        )}
      </fieldset>
    </div>
  </div>
}
